import { useId, useMemo, useState } from 'react';

import { Button, Dialog, Field, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayVN } from '../../../lib/dinh-dang';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { ChonQuyen } from '../../vai-tro/components/ChonQuyen';
import { useDanhMucQuyen } from '../../vai-tro/hooks/useVaiTro';
import { MA_QUYEN_NEN_TANG, loiTheoConTro, sapTheoDanhMuc } from '../../vai-tro/nghiep-vu/vai-tro';
import { HAN_MAC_DINH, LUA_CHON_HAN } from '../constants';
import type { HanDung } from '../nghiep-vu/khoa-api';
import { TEN_TOI_DA, hanThanhIso, kiemTen, loiTheoPhamVi } from '../nghiep-vu/khoa-api';
import type { ApiKeyCreate } from '../types';

/** Đường dẫn lỗi có ô trên form — lỗi khác gom vào dải chung. */
const O_CO_TREN_FORM = /^\/(name|expires_at|scopes(\/\d+)?)$/;

/**
 * Hộp tạo khoá API (`POST /api/v1/api-keys`): tên hệ thống dùng khoá, quyền
 * chọn từ cùng danh mục với vai trò lớp 2, hạn dùng.
 *
 * Lỗi của server đặt cạnh chỗ sai theo `pointer`: quyền không gán được (`422`,
 * `/scopes/{i}`) dưới đúng ô quyền, tên và hạn dưới ô của chúng. Hộp không
 * đóng khi lỗi.
 */
export function HopTaoKhoaApi({
  open,
  dangGui,
  onClose,
  onTao,
}: {
  open: boolean;
  dangGui: boolean;
  onClose: () => void;
  onTao: (du: ApiKeyCreate) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const danhMuc = useDanhMucQuyen(open);
  // Khoá API chỉ gọi API của module (ADR-0068): quyền nền tảng `core` (ADR-0104) gán cho vai trò, không cho khoá.
  const quyenKhoa = useMemo(
    () => danhMuc.data?.filter((q) => q.module_code !== MA_QUYEN_NEN_TANG),
    [danhMuc.data],
  );

  const [ten, setTen] = useState('');
  const [han, setHan] = useState<HanDung>(HAN_MAC_DINH);
  const [daChon, setDaChon] = useState<Set<string>>(() => new Set());
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{
    loi: ApiError;
    truong: Record<string, string>;
    quyen: Record<string, string>;
  } | null>(null);

  const maTen = kiemTen(ten);
  const loiTen =
    (maTen && (daGui || maTen === 'dai')
      ? t(maTen === 'trong' ? 'khoaApi.fTenTrong' : 'khoaApi.fTenDai', { so: TEN_TOI_DA })
      : undefined) ?? loiServer?.truong['/name'];
  const loiQuyen =
    (daGui && daChon.size === 0 ? t('khoaApi.fQuyenTrong') : undefined) ?? loiServer?.truong['/scopes'];
  const loiHan = loiServer?.truong['/expires_at'];
  const loiKhongCoO = Object.entries(loiServer?.truong ?? {}).filter(([d]) => !O_CO_TREN_FORM.test(d));
  // Lỗi đã nằm cạnh từng ô thì dải chung chỉ nhắc một câu, không lặp chi tiết.
  const coLoiTheoO = loiServer && Object.keys(loiServer.truong).length > 0 && loiKhongCoO.length === 0;

  const ngayHet = hanThanhIso(han, new Date());

  const gui = async () => {
    setDaGui(true);
    if (maTen || daChon.size === 0 || dangGui) return;
    const scopes = sapTheoDanhMuc(daChon, quyenKhoa ?? []);
    const du: ApiKeyCreate = { name: ten.trim(), scopes, expires_at: hanThanhIso(han, new Date()) };
    setLoiServer(null);
    try {
      await onTao(du);
    } catch (err) {
      const truong = loiTheoConTro((err as ApiError).details);
      setLoiServer({ loi: err as ApiError, truong, quyen: loiTheoPhamVi(truong, scopes) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('khoaApi.taoTieuDe')}
      width={640}
      destroyOnHidden
      footer={
        // Chân hộp của antd căn phải; dải lỗi nhiều dòng phải đọc từ trái.
        <div className="space-y-3 text-left">
          {loiServer && (
            <LoiTrongHop>
              {coLoiTheoO ? t('khoaApi.loiTheoO') : dichLoi(loiServer.loi)}
              {loiKhongCoO.map(([duongDan, thongDiep]) => (
                <span key={duongDan} className="mt-1 block">
                  <span className="font-mono">{duongDan}</span>: {thongDiep}
                </span>
              ))}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('khoaApi.dangTao') : t('khoaApi.taoKhoa')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('khoaApi.fTen')} required hint={t('khoaApi.fTenGoiY')} error={loiTen}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                autoComplete="off"
                value={ten}
                onChange={(e) => {
                  setTen(e.target.value);
                  setLoiServer(null);
                }}
              />
            )}
          </Field>
          <Field
            label={t('khoaApi.fHan')}
            hint={ngayHet ? t('khoaApi.fHanGoiY', { ngay: ngayVN(ngayHet) }) : t('khoaApi.fHanKhongGoiY')}
            error={loiHan}
          >
            {(p) => (
              <Select<HanDung>
                {...p}
                className="w-full"
                value={han}
                onChange={(v) => {
                  setHan(v);
                  setLoiServer(null);
                }}
                options={LUA_CHON_HAN.map((l) => ({ value: l.gia, label: t(l.khoa) }))}
              />
            )}
          </Field>
        </div>

        <section className="space-y-2" aria-labelledby={`${formId}-quyen`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 id={`${formId}-quyen`} className="text-sm font-medium text-content-secondary">
              {t('khoaApi.fQuyen')}
            </h3>
            <span className="text-xs text-content-muted">{t('khoaApi.daChonSoQuyen', { so: daChon.size })}</span>
          </div>
          <p className="text-xs text-content-muted">{t('khoaApi.fQuyenGoiY')}</p>
          {loiQuyen && (
            <p role="alert" className="text-xs text-danger-700">
              {loiQuyen}
            </p>
          )}
          <div className="max-h-80 overflow-y-auto pr-1">
            <ChonQuyen
              danhMuc={quyenKhoa}
              dangTai={danhMuc.isLoading}
              loi={danhMuc.error}
              onThuLai={() => void danhMuc.refetch()}
              daChon={daChon}
              onDoi={(moi) => {
                setDaChon(moi);
                // Đã sửa lựa chọn thì lỗi theo quyền của lần gửi trước không còn đúng chỗ.
                if (loiServer) setLoiServer(null);
              }}
              loiTheoQuyen={loiServer?.quyen}
            />
          </div>
        </section>
      </form>
    </Dialog>
  );
}
